import { idb } from "../db/index.ts";
import { g, logEvent } from "../util/index.ts";
import { getClient, getLeagueIdForLid } from "./index.ts";

// ============================================================================
// Synced lineups (v11). The sim runs on the runner's machine, so a player's
// local roster tweaks (order, playing time, play-through-injury) would never
// reach the games without syncing. When you ready up, your team's settings are
// captured into a 'lineup' event that every client (runner included) applies
// deterministically - so the playoffs (and any sim) use YOUR lineup.
// ============================================================================

export type LineupPayload = {
	tid: number;
	keepRosterSorted: boolean;
	playThroughInjuries: [number, number];
	// Position depth chart (per-sport shape, e.g. { G, F, C } for basketball)
	depth?: Record<string, number[]>;
	// Team finances (spending levels + ticket price) - synced so a non-host's
	// budget survives the runner's sim, and so the sim uses it (facilities etc.
	// affect development/health). Undefined only for events from older clients.
	budget?: Record<string, number>;
	adjustForInflation?: boolean;
	autoTicketPrice?: boolean;
	players: {
		pid: number;
		rosterOrder: number;
		ptModifier: number;
	}[];
};

export const captureMyLineup = async (): Promise<LineupPayload | undefined> => {
	const tid = g.get("userTid");
	const t = await idb.cache.teams.get(tid);
	if (!t) {
		return undefined;
	}
	const players = await idb.cache.players.indexGetAll("playersByTid", tid);
	return {
		tid,
		keepRosterSorted: !!t.keepRosterSorted,
		playThroughInjuries: t.playThroughInjuries ?? [0, 0],
		depth: t.depth as Record<string, number[]> | undefined,
		budget: t.budget as Record<string, number> | undefined,
		adjustForInflation: t.adjustForInflation,
		autoTicketPrice: t.autoTicketPrice,
		players: players.map((p) => ({
			pid: p.pid,
			rosterOrder: p.rosterOrder ?? 0,
			ptModifier: p.ptModifier ?? 1,
		})),
	};
};

// Sent right before readying up; failures warn but never block the ready
export const submitMyLineup = async (leagueId: string) => {
	try {
		const payload = await captureMyLineup();
		if (!payload) {
			return;
		}
		const { data, error } = await getClient().rpc("submit_lineup", {
			p_league_id: leagueId,
			p_payload: payload,
		});
		if (error) {
			throw new Error(error.message);
		}
		if (data.status !== "ok") {
			throw new Error(data.status);
		}
	} catch (error) {
		logEvent({
			type: "error",
			text: `Syncing your lineup failed (${error.message}) - the sim may use your previous roster settings.`,
			saveToDb: false,
		});
	}
};

// Called (debounced) whenever a human edits their lineup - roster order, playing
// time, play-through-injury - in an MP league. v11 only synced lineups at
// ready-up, so a change made afterwards (e.g. adjusting your team between
// playoff games) never reached the runner's sim; the runner would have to
// god-mode edit your team. Now any edit re-submits your lineup as an event that
// the runner applies before the next games. Debounced so dragging players
// around coalesces into one event.
let lineupSyncTimer: ReturnType<typeof setTimeout> | undefined;
export const mpMaybeSyncLineup = () => {
	if (lineupSyncTimer) {
		clearTimeout(lineupSyncTimer);
	}
	lineupSyncTimer = setTimeout(async () => {
		lineupSyncTimer = undefined;
		const lid = g.get("lid");
		if (typeof lid !== "number") {
			return;
		}
		try {
			const leagueId = await getLeagueIdForLid(lid);
			if (leagueId) {
				await submitMyLineup(leagueId);
			}
		} catch {
			// Best-effort; a failed sync just means the runner uses the last one
		}
	}, 1500);
};

// Deterministic application of a lineup event (all clients, creator included)
export const applyLineupEvent = async (payload: LineupPayload) => {
	const t = await idb.cache.teams.get(payload.tid);
	if (t) {
		t.keepRosterSorted = payload.keepRosterSorted;
		t.playThroughInjuries = payload.playThroughInjuries;
		if (payload.depth) {
			t.depth = payload.depth as typeof t.depth;
		}
		if (payload.budget) {
			t.budget = payload.budget as typeof t.budget;
		}
		if (payload.adjustForInflation !== undefined) {
			t.adjustForInflation = payload.adjustForInflation;
		}
		if (payload.autoTicketPrice !== undefined) {
			t.autoTicketPrice = payload.autoTicketPrice;
		}
		await idb.cache.teams.put(t);
	}
	for (const entry of payload.players) {
		const p = await idb.cache.players.get(entry.pid);
		// Only touch players still on that team (a stale event must not move
		// or modify players traded away since)
		if (p && p.tid === payload.tid) {
			p.rosterOrder = entry.rosterOrder;
			p.ptModifier = entry.ptModifier;
			await idb.cache.players.put(p);
		}
	}
};
